// D-SHIP-10 (Patrick 2026-08-02): audible scan feedback. Two sounds:
//   scanGood() — short rising chirp: the scan was accepted into the package.
//   scanWarn() — double low buzz: warning/error (wrong patient, duplicate, not
//                shippable, not found, check failed). Same sound for warning and
//                error by design — one unmistakable "stop and look" cue.
// WebAudio (no asset files, zero latency). The AudioContext is created lazily and
// resumed on first play; barcode scanners emit keystrokes, which count as the user
// gesture browsers require, so audio is unlocked by the act of scanning itself.
// Every call is fail-silent: sound must never break scanning.

let ctx: AudioContext | null = null

function ac(): AudioContext | null {
  try {
    if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    if (ctx.state === 'suspended') void ctx.resume()
    return ctx
  } catch { return null }
}

function tone(freqStart: number, freqEnd: number, startAt: number, dur: number, type: OscillatorType, gainPeak: number) {
  const c = ac()
  if (!c) return
  try {
    const o = c.createOscillator()
    const g = c.createGain()
    o.type = type
    const t0 = c.currentTime + startAt
    o.frequency.setValueAtTime(freqStart, t0)
    if (freqEnd !== freqStart) o.frequency.exponentialRampToValueAtTime(freqEnd, t0 + dur)
    g.gain.setValueAtTime(0.0001, t0)
    g.gain.exponentialRampToValueAtTime(gainPeak, t0 + 0.01)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    o.connect(g).connect(c.destination)
    o.start(t0)
    o.stop(t0 + dur + 0.02)
  } catch { /* never break scanning over a beep */ }
}

/** Accepted scan — short rising chirp. */
export function scanGood() {
  tone(880, 1318, 0, 0.12, 'sine', 0.08)
}

/** Warning or error — double low buzz that cuts through pharmacy noise. */
export function scanWarn() {
  tone(311, 311, 0, 0.14, 'square', 0.06)
  tone(233, 233, 0.18, 0.18, 'square', 0.07)
}

// D-SHIP-17 (Patrick 2026-08-03): active-shipment alert for dispatch. Distinct
// from scanWarn() — a brighter triple triangle-note that reads as "there is an
// active shipment for this patient, decide" rather than "bad scan." Fires ONCE
// when the active-shipment panel appears. Fail-silent like the rest of this file.
/** Active-shipment alert — bright triple note, deliberately unlike scanWarn(). */
export function shipmentAlert() {
  tone(660, 660, 0,    0.10, 'triangle', 0.09)
  tone(880, 880, 0.14, 0.10, 'triangle', 0.09)
  tone(660, 660, 0.28, 0.16, 'triangle', 0.10)
}
